import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { Apple, LifeBuoy, HeartHandshake, Shirt, CalendarHeart, Globe2, ArrowUpRight, PawPrint, PartyPopper } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { PARTNER } from "@/i18n/extra_translations";

const PANTRY_SLIDES = [
  "/images/pantry-food/3.jpg",
  "/images/pantry-food/1.jpg",
  "/images/pantry-food/2.jpg",
  "/images/pantry-food/4.jpg",
  "/images/pantry-food/5.jpg",
];

const KIA_SLIDES = [
  "/images/kia/3.jpg",
  "/images/kia/1.jpg",
  "/images/kia/2.jpg",
  "/images/kia/4.jpg",
  "/images/kia/5.jpg",
  "/images/kia/7.jpg",
  "/images/kia/8.jpg",
];

const FAMILY_SLIDES = [
  "/images/family-support/1.jpg",
  "/images/family-support/2.jpg",
  "/images/family-support/3.jpg",
  "/images/family-support/4.jpg",
];

const IMG = {
  disaster: "/images/supply-run.jpg",
  clothing: "/images/toys.jpg",
  events: "/images/community-group.jpg",
};

const cardClass =
  "group relative block h-full min-h-[20rem] rounded-2xl border border-[#E2E8E4] overflow-hidden transition-all duration-500 hover:border-[#F4A261]/60 hover:-translate-y-1";

const Overlay = ({ icon: Icon, title, text }) => (
  <>
    <div className="absolute inset-0 bg-gradient-to-t from-[#1A2F24]/90 via-[#1A2F24]/30 to-transparent" />
    <div className="absolute inset-0 flex flex-col justify-end p-8">
      <Icon size={22} className="text-[#F4A261] mb-3" />
      <h3 className="font-display text-3xl text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-white/80 max-w-md">{text}</p>
    </div>
    <ArrowUpRight size={20} className="absolute top-6 right-6 text-white/70 transition-transform duration-500 group-hover:rotate-45" />
  </>
);

const ImageCard = ({ img, icon, title, text, span, testId, to, imgClass = "object-cover object-center" }) => (
  <Reveal className={`col-span-12 ${span}`} delay={0.05}>
    <Link to={to} data-testid={testId} className={cardClass}>
      <img src={img} alt={title} className={`absolute inset-0 w-full h-full ${imgClass} transition-transform duration-700 group-hover:scale-105`} />
      <Overlay icon={icon} title={title} text={text} />
    </Link>
  </Reveal>
);

const SlideshowCard = ({ images, icon, title, text, span, testId, to }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setIdx((i) => (i + 1) % images.length), 4000);
    return () => clearInterval(timer);
  }, [images.length]);
  return (
    <Reveal className={`col-span-12 ${span}`} delay={0.05}>
      <Link to={to} data-testid={testId} className="group relative block h-full min-h-[30rem] md:min-h-[34rem] rounded-2xl border border-[#E2E8E4] overflow-hidden bg-[#1A2F24] transition-all duration-500 hover:border-[#F4A261]/60 hover:-translate-y-1">
        {images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={title}
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-[1200ms] ease-in-out ${i === idx ? "opacity-100" : "opacity-0"}`}
          />
        ))}
        <Overlay icon={icon} title={title} text={text} />
      </Link>
    </Reveal>
  );
};

const Programs = () => {
  const { t, lang } = useLanguage();
  const partner = PARTNER[lang] || PARTNER.en;
  return (
  <section id="programs" className="py-28 lg:py-36 bg-[#F2F4F0]/50 border-y border-[#E2E8E4]/70" data-testid="programs-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-16">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C7368] mb-6">{t("prog_kicker")}</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A2F24]">{t("prog_title_a")}<em className="text-[#F4A261]">{t("prog_title_em")}</em></h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-[#5C7368]">
          {t("prog_intro")}
        </p>
      </Reveal>
      <div className="grid grid-cols-12 gap-6 items-start">
        <SlideshowCard images={PANTRY_SLIDES} span="md:col-span-8" icon={Apple} title={t("p1_t")}
          text={t("p1_x")} testId="program-food-pantry" to="/programs/food-pantry" />
        <SlideshowCard images={FAMILY_SLIDES} span="md:col-span-4" icon={HeartHandshake} title={t("p2_t")}
          text={t("p2_x")} testId="program-family-support" to="/programs/family-disability-support" />
        <ImageCard span="md:col-span-4" img={IMG.clothing} icon={Shirt} title={t("p3_t")}
          text={t("p3_x")} testId="program-clothing-supplies" to="/programs/clothing-toys-supplies" />
        <ImageCard span="md:col-span-8" img={IMG.disaster} icon={LifeBuoy} title={t("p4_t")}
          text={t("p4_x")} testId="program-disaster-relief" to="/programs/disaster-relief" />
        <ImageCard span="md:col-span-7" img={IMG.events} icon={CalendarHeart} title={t("p5_t")}
          text={t("p5_x")} testId="program-community-events" to="/programs/community-events" />
        <ImageCard span="md:col-span-5" img="/images/international.jpg" icon={Globe2} title={t("p6_t")}
          text={t("p6_x")} testId="program-international" to="/programs/international-outreach" />
        <SlideshowCard images={KIA_SLIDES} span="md:col-span-12" icon={PartyPopper} title={t("p8_t")}
          text={t("p8_x")} testId="program-charity-events" to="/programs/charity-fundraiser-events" />
        <Reveal className="col-span-12" delay={0.05}>
          <div data-testid="program-animal-outreach"
            className="group rounded-2xl border border-[#E2E8E4] overflow-hidden transition-all duration-500 hover:border-[#F4A261]/60 hover:-translate-y-1 grid grid-cols-12">
            <div className="col-span-12 md:col-span-4 relative min-h-[16rem]">
              <img src="/images/puppy.jpg" alt={partner.p7_t} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-testid="animal-outreach-puppy" />
            </div>
            <div className="hidden md:block md:col-span-4 relative min-h-[16rem]">
              <img src="/images/smallest-souls-event.jpg" alt="Smallest Souls Rescue adoption event tent" className="absolute inset-0 w-full h-full object-cover" data-testid="animal-outreach-event" />
            </div>
            <div className="col-span-12 md:col-span-4 bg-[#1E352A] p-8 flex flex-col justify-between gap-6">
              <div>
                <PawPrint size={24} className="text-[#F4A261]" />
                <h3 className="font-display text-3xl text-[#FAF9F6] mt-3">{partner.p7_t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#C4D7D1]">{partner.p7_x}</p>
              </div>
              <a href="https://www.smallestsoulsanimalrescue.com/" target="_blank" rel="noopener noreferrer" data-testid="smallest-souls-link"
                className="inline-flex items-center gap-2 self-start rounded-full bg-[#F4A261] text-white px-6 py-2.5 text-xs tracking-[0.12em] uppercase font-semibold transition-colors duration-300 hover:bg-[#C4D7D1] hover:text-[#1E352A]">
                {partner.p7_link} <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

export default Programs;
